import { useMemo, useState, useCallback } from 'react';

import useLocales from './useLocales';
import type { FormContent, Collaborator, ElementProps } from '../@types/forms/types';
import type {
  FieldErrors,
  CollaboratorFieldsGroup,
} from '../sections/forms/utils/formValidationUtils';
import {
  validateElement,
  getRequiredFields,
  getAssignedElements,
  getVisibleFieldErrors,
  getFieldsGroupedByCollaborator,
} from '../sections/forms/utils/formValidationUtils';

interface UseFormValidationProps {
  formStructure: string | undefined;
  parsedElements: ElementProps[];
  content: FormContent[];
  collaborators: Collaborator[];
  userEmail: string | undefined;
}

interface UseFormValidationReturn {
  fieldErrors: FieldErrors;
  setFieldErrors: React.Dispatch<React.SetStateAction<FieldErrors>>;
  validateFormFields: (validateAll?: boolean) => boolean;
  requiredFields: Array<{ id: string; label: string; page: number }>;
  visibleFieldErrors: FieldErrors;
  focusFieldId: string | null;
  setFocusFieldId: React.Dispatch<React.SetStateAction<string | null>>;
  handleRequiredFieldClick: (fieldId: string) => void;
  fieldsGroupedByCollaborator: CollaboratorFieldsGroup[];
}

/**
 * Custom hook for form validation logic
 * Handles field validation, required fields, and error management
 */
export const useFormValidation = ({
  formStructure,
  parsedElements,
  content,
  collaborators,
  userEmail,
}: UseFormValidationProps): UseFormValidationReturn => {
  const { t } = useLocales();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // Get required fields for current user/collaborator
  const requiredFields = useMemo(
    () => getRequiredFields(parsedElements, userEmail, collaborators),
    [parsedElements, userEmail, collaborators]
  );

  // Filter field errors to only show errors for fields visible to current user
  const visibleFieldErrors = useMemo(
    () => getVisibleFieldErrors(fieldErrors, requiredFields),
    [fieldErrors, requiredFields]
  );

  // Validate form fields (called on submit)
  // validateAll: true = validate all fields (for Generate), false = validate only current user's fields (for Save)
  const validateFormFields = useCallback(
    (validateAll = false) => {
      if (!formStructure || parsedElements.length === 0) return true;

      const errors: FieldErrors = {};

      // Get elements to validate - all elements or only assigned to current user
      const elementsToValidate = validateAll
        ? parsedElements
        : getAssignedElements(parsedElements, userEmail, collaborators);

      // Validate each element
      elementsToValidate.forEach((element) => {
        const error = validateElement(element, content, t);
        if (error) {
          errors[element.id] = error;
        }
      });

      setFieldErrors(errors);
      return Object.keys(errors).length === 0;
    },
    [formStructure, parsedElements, content, collaborators, userEmail, t]
  );

  // Handle clicking on a required field in the table
  const handleRequiredFieldClick = useCallback((fieldId: string) => {
    setFocusFieldId(fieldId);
  }, []);

  // Get fields grouped by collaborator for display
  const fieldsGroupedByCollaborator = useMemo(
    () => getFieldsGroupedByCollaborator(parsedElements, collaborators, userEmail),
    [parsedElements, collaborators, userEmail]
  );

  return {
    fieldErrors,
    setFieldErrors,
    validateFormFields,
    requiredFields,
    visibleFieldErrors,
    focusFieldId,
    setFocusFieldId,
    handleRequiredFieldClick,
    fieldsGroupedByCollaborator,
  };
};
